import { useEffect, useState } from "react";
import { Button, Sheet, Skeleton } from "../../components/ui";
import { navigate } from "../../router";
import { useInstruments, useTradingState } from "../../state/market";
import { Chart } from "./Chart";
import { MarketBar } from "./MarketBar";
import { Positions } from "./Positions";
import { Tape } from "./Tape";
import { Ticket } from "./Ticket";
import { setTicket, type Side } from "./ticket-state";
import "./trade.css";

function useWide(query = "(min-width: 1024px)") {
  const [wide, setWide] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setWide(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);
  return wide;
}

export function Trade({ coin }: { coin: string }) {
  const state = useTradingState();
  const instruments = useInstruments();
  const instrument = instruments.find((item) => item.symbol === coin);
  const wide = useWide();
  const [sheet, setSheet] = useState<Side | null>(null);

  // an unknown market goes to the first listed one
  useEffect(() => {
    const first = instruments[0];
    if (instruments.length && !instrument && first) navigate(`/trade/${first.symbol}`, true);
  }, [instruments, instrument]);

  if (!instrument) {
    return (
      <main className="tr">
        <div className="tr-loading">
          {state.isError ? (
            <p className="muted">The relayer did not answer. Retrying…</p>
          ) : (
            <>
              <Skeleton width="40%" height={32} />
              <Skeleton height={360} />
            </>
          )}
        </div>
      </main>
    );
  }

  const open = (side: Side) => {
    setTicket({ side });
    setSheet(side);
  };

  return (
    <main className="tr">
      <div className="tr-market">
        <MarketBar instrument={instrument} instruments={instruments} />
      </div>
      <div className="tr-chart panel panel-flush">
        <Chart instrument={instrument} />
      </div>
      {wide && (
        <aside className="tr-side">
          <div className="panel tr-ticket">
            <Ticket instrument={instrument} />
          </div>
          <Tape symbol={instrument.symbol} />
        </aside>
      )}
      <div className="tr-positions">
        <Positions instrument={instrument} />
      </div>
      {!wide && (
        <>
          <div className="tr-tape-mobile">
            <Tape symbol={instrument.symbol} />
          </div>
          <div className="tr-dock">
            <Button tone="up" size="lg" block onClick={() => open("long")}>
              Long
            </Button>
            <Button tone="down" size="lg" block onClick={() => open("short")}>
              Short
            </Button>
          </div>
          <Sheet open={sheet !== null} onClose={() => setSheet(null)} title={`Trade ${instrument.symbol}`}>
            <Ticket instrument={instrument} onSent={() => setSheet(null)} />
          </Sheet>
        </>
      )}
    </main>
  );
}
